import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'

export const PRODUCT_DRAWINGS_QUERY_KEY = 'productDrawings' as const

export type ProductDrawingFile = {
  id: string
  fileName: string
  contentType: string | null
  fileSizeKb: number | null
  uploadedAt: string
  uploadedByUserId: string | null
}

export type ProductDrawingsView = {
  files: ProductDrawingFile[]
}

export function useGetProductDrawings(
  productId: string | undefined,
  options?: { enabled?: boolean }
) {
  const hasProductId = productId != null && productId.length > 0
  const queryEnabled = hasProductId && (options?.enabled ?? true)

  return useQuery<ProductDrawingsView, Error>({
    queryKey: [PRODUCT_DRAWINGS_QUERY_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<ProductDrawingsView>(
        `/v2/products/${productId}/rohs/compliance-file/slots/DRAWINGS_SCHEMAS`
      )
      return response.data
    },
    enabled: queryEnabled,
  })
}

export function useUploadProductDrawing(productId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('fileName', file.name)
      const response = await apiClient.post<ProductDrawingsView>(
        `/v2/products/${productId}/rohs/compliance-file/slots/DRAWINGS_SCHEMAS`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PRODUCT_DRAWINGS_QUERY_KEY, productId], data)
    },
  })
}

export function useDeleteProductDrawing(productId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const response = await apiClient.delete<ProductDrawingsView>(
        `/v2/products/${productId}/rohs/compliance-file/slots/DRAWINGS_SCHEMAS/${attachmentId}`
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData([PRODUCT_DRAWINGS_QUERY_KEY, productId], data)
    },
  })
}

export function isProductDrawingsFilled(
  drawings: ProductDrawingsView | undefined
): boolean {
  return (drawings?.files.length ?? 0) > 0
}
